import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  syncPending,
  pendingCount as getPendingCount,
  getPendingActions,
} from '../lib/offlineStore';
import { Cloud, CloudOff, RefreshCcw, Check, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

/**
 * OfflineSyncBanner — shown at the top of FieldView. Displays:
 *   - Online / Offline status
 *   - Pending action count
 *   - Sync button (also auto-syncs when connection returns)
 *
 * Calls `onSynced()` after a successful sync so the parent can refresh.
 */
export default function OfflineSyncBanner({ onSynced }) {
  const { token } = useAuth();
  const [online, setOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [pending, setPending] = useState(getPendingCount());
  const [syncing, setSyncing] = useState(false);

  const refreshPending = useCallback(() => setPending(getPendingCount()), []);

  const doSync = useCallback(
    async ({ silent = false } = {}) => {
      if (!token) return;
      if (getPendingCount() === 0) {
        if (!silent) toast.success('Nothing to sync');
        return;
      }
      setSyncing(true);
      try {
        const result = await syncPending(token);
        refreshPending();
        if (result.synced > 0) {
          toast.success(`Synced ${result.synced} change${result.synced === 1 ? '' : 's'}`);
          onSynced?.();
        }
        if (result.failed > 0) {
          toast.error(
            `${result.failed} change${result.failed === 1 ? '' : 's'} couldn't sync and were dropped`
          );
        }
        if (result.remaining > 0 && !silent) {
          toast.message(`${result.remaining} change(s) will retry when online`);
        }
      } catch (e) {
        if (!silent) toast.error('Sync failed');
      } finally {
        setSyncing(false);
      }
    },
    [token, onSynced, refreshPending]
  );

  // Detect online/offline transitions
  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      // Auto-sync when connection returns
      doSync({ silent: true });
    };
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, [doSync]);

  // Refresh pending count when storage changes (e.g., other tabs)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'rw_pending_actions') refreshPending();
    };
    window.addEventListener('storage', onStorage);
    // Poll lightly — pending count changes from same-tab actions
    const id = setInterval(refreshPending, 2000);
    return () => {
      window.removeEventListener('storage', onStorage);
      clearInterval(id);
    };
  }, [refreshPending]);

  // Hide the banner when fully synced + online
  const isCleanOnline = online && pending === 0 && !syncing;
  if (isCleanOnline) return null;

  return (
    <div
      className={`rounded-md border p-2 flex items-center gap-2 text-sm ${
        online
          ? 'bg-blue-50 border-blue-200 text-blue-900'
          : 'bg-amber-50 border-amber-200 text-amber-900'
      }`}
      data-testid="offline-sync-banner"
    >
      {online ? (
        <Cloud className="h-4 w-4 flex-shrink-0" />
      ) : (
        <CloudOff className="h-4 w-4 flex-shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <p className="font-medium leading-tight">
          {online ? 'Online' : 'Working offline'}
        </p>
        <p className="text-xs leading-tight" data-testid="offline-pending-count">
          {pending > 0
            ? `${pending} change${pending === 1 ? '' : 's'} waiting to sync`
            : 'No pending changes'}
        </p>
      </div>
      {pending > 0 && (
        <Badge
          variant="outline"
          className={online ? 'border-blue-300' : 'border-amber-300'}
          data-testid="offline-pending-badge"
        >
          {pending}
        </Badge>
      )}
      <Button
        size="sm"
        variant="outline"
        onClick={() => doSync()}
        disabled={!online || syncing || pending === 0}
        data-testid="offline-sync-btn"
      >
        {syncing ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : pending === 0 ? (
          <Check className="h-3.5 w-3.5" />
        ) : (
          <RefreshCcw className="h-3.5 w-3.5" />
        )}
        <span className="ml-1 hidden sm:inline">
          {syncing ? 'Syncing' : pending === 0 ? 'Synced' : 'Sync now'}
        </span>
      </Button>
    </div>
  );
}

// Optional inline chip per-job to show a queued mutation
export function PendingSyncChip({ bookingId }) {
  const [hasPending, setHasPending] = useState(false);
  useEffect(() => {
    const check = () => setHasPending(getPendingActions().some((a) => a.bookingId === bookingId));
    check();
    const id = setInterval(check, 1500);
    return () => clearInterval(id);
  }, [bookingId]);
  if (!hasPending) return null;
  return (
    <Badge
      variant="outline"
      className="bg-amber-50 border-amber-300 text-amber-800 text-[10px] gap-1"
      data-testid={`pending-sync-${bookingId}`}
    >
      <AlertTriangle className="h-2.5 w-2.5" /> queued
    </Badge>
  );
}
